import { createSelector } from "reselect";

import type { RootState } from "~/app/store";
import { selectPackageEntryMap } from "~/features/cart/cart/redux/selectors/cartSelectors";
import { selectCart } from "~/features/cart/cart/redux/selectors/groupedCartSelectors";
import type { ProductTableViewModel } from "~/features/cart/cartV2/models/ProductTableViewModel";
import { buildAlternateRows, type StoredAlternateRow } from "~/features/cart/cartV2/redux/cartAlternatesSlice";
import {
  buildOptionalPartDescriptorMap,
  buildProductTableViewModel,
  collectAllSubLineItems,
} from "~/features/cart/cartV2/utils/buildProductTableViewModel";
import { buildTableId } from "~/features/cart/cartV2/utils/buildTableId";
import { selectResolvedGroupedEntryV2 } from "~/features/cart/cartV2/utils/selectResolvedGroupedEntryV2";

function resolveEntries(
  state: RootState,
  group: ReturnType<typeof selectCart>["orderables"] | ReturnType<typeof selectCart>["bundles"][number],
) {
  return group.entries.map((entry) => selectResolvedGroupedEntryV2(entry)(state));
}

export const selectOrderablesViewModel = (state: RootState): ProductTableViewModel => {
  const cart = selectCart(state);
  const group = cart.orderables;
  const tableId = buildTableId(group);

  const storedRows: StoredAlternateRow[] = state.cartAlternates.rowsByTableId[tableId] ?? [];
  const entryIds = group.entries.map((e) => e.cartEntryId);
  const alternateRows = buildAlternateRows({ tableId, storedRows, entryIds });

  const resolvedEntries = resolveEntries(state, group);
  const packageEntryMap = selectPackageEntryMap(state);
  const allSubLineItems = collectAllSubLineItems(resolvedEntries, packageEntryMap);
  const optionalPartDescriptors = buildOptionalPartDescriptorMap(state, allSubLineItems);

  return buildProductTableViewModel({
    group,
    mode: "edit",
    resolvedEntries,
    packageEntryMap,
    optionalPartDescriptors,
    alternateRows,
  });
};

export const selectBundleViewModels = createSelector(
  selectCart,
  selectPackageEntryMap,
  (state: RootState) => state,
  (cart, _packageEntryMap, state): ProductTableViewModel[] =>
    cart.bundles.map((bundle) => {
      const resolvedEntries = resolveEntries(state, bundle);
      const packageEntryMap = selectPackageEntryMap(state);
      const allSubLineItems = collectAllSubLineItems(resolvedEntries, packageEntryMap);
      const optionalPartDescriptors = buildOptionalPartDescriptorMap(state, allSubLineItems);

      return buildProductTableViewModel({
        group: bundle,
        mode: "edit",
        resolvedEntries,
        packageEntryMap,
        optionalPartDescriptors,
        alternateRows: [],
      });
    }),
);
